import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Widget from './widget';
import { tabs } from './config';
import { Style } from './style';
import TabStake from './components/tab';
import Details from './widgets/details';
import Roaster from './widgets/dds-roaster';
import NoFocusScp from './widgets/no-focus-scp';
import SupplyChain from './widgets/supply-chain';
import RecentEvents from './widgets/recent-events';
import RiskOverview from './sections/risk-overview';
import Determination from './widgets/determination';
import AssociatedEvents from './widgets/event-trend';
import { DashboardService } from '../../../services';
import SubjectAnalysis from './sections/subject-analysis';
import ProposedMitigation from './widgets/proposed-mitigation';
import InformationCompleteness from './widgets/information-completeness';

const _default = {
  supplyChainDetails: {
    "associatedCountries": [],
    "countriesOfMineralTransit": [],

    "analysis": {
      "locations": [],
      "stakeholders": []
    },
    "indicators": {
      "mineSites": {},
      "supplyChianParticipants": {},
      "proposedCorrectiveMeasures": 0,
    },
    "dueDiligenceRoster": {},
    "eventTrend": {},
    "recommendations": {}
  }
};

export default function SupplyChainSummaryProject({
  projectData = null,
  datastakeId = '',
  supplyChainMapCallback = () => { },
  openSettings = () => { },
  sidebarCollapsed = true,
  user = {},
  selectedProject,
  organisationName = null,
  language,
  filterKey,
  defaultSources = 0,
  data: allData,
  setFocusParticipant = () => { },
}) {
  const [activeTab, setActiveTab] = useState(Object.keys(tabs)[0]);
  const [showSubjectAnalysis, setShowSubjectAnalysis] = useState(false);
  const [showAssociatedEvents, setShowAssociatedEvents] = useState(false);
  const { t } = useTranslation();
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const projData = { ...data };
  const tabsRef = useRef();
  const sectionsRef = useRef();

  const settings = (user?.settings?.projects || []).find((s) => s.id === projectData?.id)?.visualisation

  const _fetch = async () => {
    try {
      setIsLoading(true);
      const res = await DashboardService.getPmeProject({ widget: 'supplyChainDetails' });
      if (res.data && Object.keys(res.data).length) {
        if (typeof setFocusParticipant === 'function') {
          setFocusParticipant(res?.data?.supplyChainDetails?.agent);
        }
        setData(res.data);
      } else {
        setData(_default);
      }
      setIsLoading(false);
    } catch (err) {
      setData(_default);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setData({});
    _fetch();
  }, [selectedProject])

  const onTabsClick = (key) => {
    if (!projData.supplyChainDetails) {
      return;
    }

    if (key === 'subjectAnalysis') {
      setShowSubjectAnalysis(true);
    }

    if (key === 'associatedEvents') {
      setShowAssociatedEvents(true);
    }

    setActiveTab(key);
  };

  const loadingRiskOverview = useMemo(() => (
    !data?.supplyChainDetails
  ), [data])

  useEffect(() => {
    if (filterKey === '3b' || filterKey === '4b') {
      onTabsClick(Object.keys(tabs)[0]);
    }

    if (filterKey === '5b' || filterKey === '6b') {
      onTabsClick(Object.keys(tabs)[1]);
    }

    if (filterKey === '7b' || filterKey === '8b') {
      onTabsClick(Object.keys(tabs)[2]);
    }

    if (filterKey === '9b') {
      openSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  return (
    <Style ref={sectionsRef} className={sidebarCollapsed ? 'collapsed' : 'not-collapsed'}>
      {projectData && !settings?.focusScp ? (
        <Widget withBorderHeader={false} className="no-pb-header mb">
          <NoFocusScp openSettings={openSettings} />
        </Widget>
      ) : null}
      <section>
        <Roaster
          projectData={projectData}
          data={projData.supplyChainDetails?.dueDiligenceRoster}
          allData={allData}
          defaultSources={defaultSources}
        />
      </section>
      <section>
        <Details
          data={projData.supplyChainDetails}
          options={projData.options}
        />
        <Determination
          data={projData.supplyChainDetails}
          projectData={projectData}
          options={projData.options}
        />
      </section>
      <section>
        <InformationCompleteness
          loading={!projData?.supplyChainDetails}
          data={projData.supplyChainDetails?.dueDiligenceProgress}
          options={projData.options}
        />
      </section>
      <section>
        <SupplyChain
          data={{
            trade: data.trade,
            scm: data.scm,
            map: data.map,
            locations: projData.locations,
            stakeholders: projData.stakeholders,
          }}
          isLoading={isLoading}
          filterKey={filterKey}
          openSettings={openSettings}
          datastakeId={datastakeId}
          supplyChainMapCallback={supplyChainMapCallback}
          selectedProject={selectedProject}
          projectData={projectData}
        />
      </section>
      <div className='tabs-cont' id="3b">
        <section ref={tabsRef}>
          <Tabs activeKey={activeTab}
            className="risk-tabs"
            onChange={(key) => onTabsClick(key)}
            items={Object.entries(tabs).map(([k, v]) => ({
              key: k,
              disabled: !projData.supplyChainDetails,
              label: (
                  <TabStake
                    t={t}
                    config={tabs[k]}
                    key={k}
                    isActive={k === activeTab}
                    onClick={() => onTabsClick(k, v)}
                  />
              )
            }))}
          />
        </section>
        <div style={{ display: activeTab === 'riskOverview' ? 'block' : 'none' }}>
          <RiskOverview
            provenanceRisk={data?.supplyChainDetails?.provenanceRisk}
            selectedProject={selectedProject}
            supplyChainRisk={data?.supplyChainDetails?.supplyChainRisk}
            assessed={projData?.supplyChainDetails?.dueDiligenceProgress?.dueDiligenceUptake?.assessed}
            loading={loadingRiskOverview}
          />
          <ProposedMitigation
            project={projectData}
            data={data.supplyChainDetails?.recommendations}
            organisationName={organisationName}
            allEvents={data.allEvents}
            language={language}
          />
        </div>
        {showSubjectAnalysis && (
          <div style={{ display: activeTab === 'subjectAnalysis' ? 'block' : 'none' }}>
            <SubjectAnalysis
              sidebarCollapsed={sidebarCollapsed}
              selectedProject={selectedProject}
              locationAnalysis={projData.supplyChainDetails?.analysis.locations}
              paritcipantAnalysis={projData.supplyChainDetails?.analysis.stakeholders}
            />
          </div>
        )}
        {showAssociatedEvents && (
          <div style={{ display: activeTab === 'associatedEvents' ? 'block' : 'none' }}>
            <section>
              <AssociatedEvents
                allData={projData?.supplyChainDetails?.eventTrend}
              />
            </section>
            <section className="no-wrap-section">
              <RecentEvents
                data={projData.recentEvents}
                sidebarCollapsed={sidebarCollapsed}
                selectedProject={selectedProject}
              />
            </section>
          </div>
        )}
      </div>
    </Style>
  );
}
